import React, { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Cancel, Refresh, Upgrade, Payment, Autorenew } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService, type Project } from '../../services/apiService';
import { getHostTenantSlug } from '../../utils/tenantResolver';
import {
  getManageAccountCacheKey,
  readManageAccountCache,
  writeManageAccountCache,
  clearManageAccountCache,
  MANAGE_ACCOUNT_SUBSCRIPTION_CHANGED_EVENT,
} from './manageAccountSessionCache';
import SubscriptionAddons, { TENANT_ADDON_REFRESH_EVENT } from './SubscriptionAddons';
import { DEFAULT_LIMIT_LABELS_BY_PLAN } from '../../config/planLimitsConfig';
import { ENVIRONMENT_VARIABLES_CHANGED_EVENT } from '../../utils/environmentVariablesEvents';

/** Viewport anchorPosition for MUI Select (stable under CSS zoom; always opens below the field). */
function useZoomSafeSelectMenu(extra?: {
  paperSx?: Record<string, unknown>;
  menuListSx?: Record<string, unknown>;
}) {
  const [menuState, setMenuState] = useState({
    open: false,
    top: 0,
    left: 0,
    width: 0,
  });
  const anchorElRef = useRef<HTMLElement | null>(null);

  const measureFromEl = (combobox: HTMLElement) => {
    const rect = combobox.getBoundingClientRect();
    return {
      top: Math.round(rect.bottom + 4),
      left: Math.round(rect.left),
      width: Math.round(rect.width),
    };
  };

  const onOpen = (e: React.SyntheticEvent) => {
    const el = e.currentTarget as HTMLElement;
    const combobox =
      (el.matches('[role="combobox"]')
        ? el
        : el.querySelector<HTMLElement>('[role="combobox"]')) ?? el;
    anchorElRef.current = combobox;
    const measured = measureFromEl(combobox);
    flushSync(() => {
      setMenuState({
        open: true,
        ...measured,
      });
    });
  };

  const onClose = () => setMenuState((prev) => ({ ...prev, open: false }));

  const remeasureWhileOpen = () => {
    const combobox = anchorElRef.current;
    if (!combobox) return;
    const measured = measureFromEl(combobox);
    setMenuState((prev) =>
      prev.open
        ? {
            ...prev,
            ...measured,
          }
        : prev
    );
  };

  const MenuProps = {
    disableScrollLock: true,
    anchorReference: 'anchorPosition' as const,
    anchorPosition: { top: menuState.top, left: menuState.left },
    transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
    sx: { zIndex: (theme: import('@mui/material/styles').Theme) => theme.zIndex.modal },
    TransitionProps: {
      onEntering: remeasureWhileOpen,
      onEntered: remeasureWhileOpen,
    },
    PaperProps: {
      sx: {
        mt: 0.5,
        overflowX: 'hidden',
        overflowY: 'hidden',
        boxSizing: 'border-box',
        zIndex: (theme: import('@mui/material/styles').Theme) => theme.zIndex.modal,
        ...(menuState.width > 0
          ? {
              width: menuState.width,
              minWidth: menuState.width,
              maxWidth: menuState.width,
            }
          : {}),
        ...extra?.paperSx,
      },
    },
    MenuListProps: {
      sx: {
        maxHeight: 40 * 6 + 16,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        py: 0.5,
        boxSizing: 'border-box',
        '& .MuiMenuItem-root': {
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        ...extra?.menuListSx,
      },
    },
  };

  return { menuOpen: menuState.open, onOpen, onClose, MenuProps };
}

type SubscriptionCache = {
  subscription: any;
  subscriptionDetails: SubscriptionDetailsPayload | null;
  planLimitLabels: PlanLimitLabelStrings | null;
  featureUsage: FeatureUsageNumbers | null;
  projects: Project[];
};

type SubscriptionDetailsPayload = {
  plan?: string;
  contactSales?: boolean;
  limits?: {
    maxProjects?: number;
    maxCollections?: number;
    maxRequests?: number;
    maxDatasets?: number;
    maxVariables?: number;
    maxEnvironments?: number;
    maxAssertions?: number;
    maxTestSuites?: number;
  };
  usage?: {
    projects?: number;
    collections?: number;
    requests?: number;
    datasets?: number;
    variables?: number;
    environments?: number;
    assertions?: number;
    testSuites?: number;
  };
  error?: string;
};

type PlanLimitLabelStrings = {
  projects?: string;
  collections?: string;
  requests?: string;
  dataSets?: string;
  variables?: string;
  environments?: string;
  assertions?: string;
  testSuites?: string;
};

type FeatureUsageNumbers = {
  projects?: number;
  collections?: number;
  requests?: number;
  datasets?: number;
  variables?: number;
  environments?: number;
  assertions?: number;
  testSuites?: number;
};

const STANDARD_TIER_PLAN_KEYS = new Set(['FREE', 'STANDARD', 'PROFESSIONAL']);

const parseLimitNumStr = (s: string | undefined): number | null => {
  if (s == null || s === '' || s === 'Unlimited') return null;
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
};

/** Per-project cap for FREE/STANDARD/PROFESSIONAL (normalize workspace totals from stale API rows). */
const resolvePerProjectResourceCap = (
  planKey: string,
  kind: 'collections' | 'requests' | 'dataSets' | 'variables' | 'assertions' | 'environments' | 'testSuites',
  limValue: number | null,
  labels: PlanLimitLabelStrings | null | undefined,
  defaults: PlanLimitLabelStrings | undefined,
  maxProjects: number | null
): number | null => {
  const fromLabel = parseLimitNumStr(labels?.[kind]);
  const fromDefault = parseLimitNumStr(defaults?.[kind]);
  let cap = fromLabel ?? fromDefault ?? limValue;
  if (cap == null || cap < 0) return cap;
  // Per-project environment caps include purchased add-on packs (+10 per pack); do not normalize down to plan defaults.
  if (kind === 'environments') {
    return cap;
  }
  if (!STANDARD_TIER_PLAN_KEYS.has(planKey) || fromDefault == null || maxProjects == null || maxProjects <= 0) {
    return cap;
  }
  const workspaceAggregate = fromDefault * maxProjects;
  if (cap === workspaceAggregate || cap > workspaceAggregate) {
    return fromDefault;
  }
  if (cap > fromDefault && maxProjects > 1 && cap % maxProjects === 0) {
    const divided = cap / maxProjects;
    if (divided === fromDefault) return fromDefault;
  }
  return cap;
};

function parseUsageBreakdown(breakdown: Record<string, unknown>): FeatureUsageNumbers {
  const toNum = (v: unknown) => {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (v != null && v !== '') {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
    return 0;
  };
  return {
    collections: toNum(breakdown.collections),
    requests: toNum(breakdown.requests),
    datasets: toNum(breakdown.dataSets ?? breakdown.datasets),
    variables: toNum(breakdown.variables),
    environments: toNum(breakdown.environments),
    assertions: toNum(breakdown.assertions),
    testSuites: toNum(breakdown.testSuites),
  };
}

function normalizeFeatureLimits(raw: unknown): {
  projects?: string;
  collections?: string;
  requests?: string;
  dataSets?: string;
  variables?: string;
  environments?: string;
  assertions?: string;
  testSuites?: string;
  usageProjects: number;
  usageCollections: number;
  usageRequests: number;
  usageDataSets: number;
  usageVariables: number;
  usageEnvironments: number;
  usageAssertions: number;
  usageTestSuites: number;
} | null {
  if (raw == null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.error) return null;
  const str = (v: unknown) => {
    if (v == null || v === '') return undefined;
    return String(v);
  };
  const num = (v: unknown, def = 0): number => {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (v != null && v !== '') {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
    return def;
  };
  const projects = str(r.projects);
  const collections = str(r.collections);
  const requests = str(r.requests);
  const dataSets = str(r.dataSets ?? r.datasets);
  const variables = str(r.variables);
  const environments = str(r.environments);
  const assertions = str(r.assertions);
  const testSuites = str(r.testSuites ?? r.test_suites);
  if (!projects && !collections && !requests && !dataSets && !variables && !environments && !assertions && !testSuites) {
    return null;
  }
  return {
    projects,
    collections,
    requests,
    dataSets,
    variables,
    environments,
    assertions,
    testSuites,
    usageProjects: num(r.usageProjects ?? r.usage_projects),
    usageCollections: num(r.usageCollections ?? r.usage_collections),
    usageRequests: num(r.usageRequests ?? r.usage_requests),
    usageDataSets: num(r.usageDataSets ?? r.usage_data_sets ?? r.usageDatasets),
    usageVariables: num(r.usageVariables ?? r.usage_variables),
    usageEnvironments: num(r.usageEnvironments ?? r.usage_environments),
    usageAssertions: num(r.usageAssertions ?? r.usage_assertions),
    usageTestSuites: num(r.usageTestSuites ?? r.usage_test_suites ?? r.usageTestsuites),
  };
}

function planLabelsFromLimits(
  limits: SubscriptionDetailsPayload['limits'] | undefined
): PlanLimitLabelStrings | null {
  if (!limits) return null;
  const f = (n: number | undefined) =>
    n == null ? undefined : n === -1 ? 'Unlimited' : String(n);
  if (
    limits.maxProjects == null &&
    limits.maxCollections == null &&
    limits.maxRequests == null &&
    limits.maxDatasets == null &&
    limits.maxVariables == null &&
    limits.maxEnvironments == null &&
    limits.maxAssertions == null &&
    limits.maxTestSuites == null
  ) {
    return null;
  }
  return {
    projects: f(limits.maxProjects),
    collections: f(limits.maxCollections),
    requests: f(limits.maxRequests),
    dataSets: f(limits.maxDatasets),
    variables: f(limits.maxVariables),
    environments: f(limits.maxEnvironments),
    assertions: f(limits.maxAssertions),
    testSuites: f(limits.maxTestSuites),
  };
}

function labelsFromAccountSubscription(response: {
  error?: string;
  subscriptionError?: string;
  maxProjects?: number;
  maxCollections?: number;
  maxRequests?: number;
  maxDatasets?: number;
  maxVariables?: number;
  maxEnvironments?: number;
  maxAssertions?: number;
  maxTestSuites?: number;
}): PlanLimitLabelStrings | null {
  if (!response || response.error || response.subscriptionError) return null;
  const lim = (n: number | undefined) =>
    n == null ? undefined : n === -1 ? 'Unlimited' : String(n);
  if (
    response.maxProjects == null &&
    response.maxCollections == null &&
    response.maxRequests == null &&
    response.maxDatasets == null
  ) {
    return null;
  }
  return {
    projects: lim(response.maxProjects),
    collections: lim(response.maxCollections),
    requests: lim(response.maxRequests),
    dataSets: lim(response.maxDatasets),
    variables: lim(response.maxVariables),
    environments: lim(response.maxEnvironments),
    assertions: lim(response.maxAssertions),
    testSuites: lim(response.maxTestSuites),
  };
}

const sidebarAwareDialogSx = (maxPx: number) => ({
  zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 6,
  '& .MuiDialog-container': {
    paddingLeft: '16px',
    paddingRight: '16px',
    boxSizing: 'border-box',
  },
  '& .MuiDialog-paper': {
    width: `min(${maxPx}px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))`,
    maxWidth: `min(${maxPx}px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))`,
    margin: 'auto',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
});

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const subscriptionCacheKey = getManageAccountCacheKey(null, getHostTenantSlug());
  const cachedSubscription = readManageAccountCache<SubscriptionCache>(
    'subscription',
    subscriptionCacheKey
  );
  const [subscription, setSubscription] = useState<any>(cachedSubscription?.subscription ?? null);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetailsPayload | null>(
    cachedSubscription?.subscriptionDetails ?? null
  );
  const [planLimitLabels, setPlanLimitLabels] = useState<PlanLimitLabelStrings | null>(
    cachedSubscription?.planLimitLabels ?? null
  );
  const [featureUsage, setFeatureUsage] = useState<FeatureUsageNumbers | null>(
    cachedSubscription?.featureUsage ?? null
  );
  const [loading, setLoading] = useState(!cachedSubscription);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [subscriptionView, setSubscriptionView] = useState<{
    readOnly?: boolean;
    validUntil?: string;
    status?: string;
    upgradeAllowed?: boolean;
  } | null>(null);
  const [processing, setProcessing] = useState(false);
  const [projects, setProjects] = useState<Project[]>(cachedSubscription?.projects ?? []);
  const [selectedProjectId, setSelectedProjectId] = useState<number | 'all'>('all');
  const [selectedProjectUsage, setSelectedProjectUsage] = useState<FeatureUsageNumbers | null>(null);
  const [projectUsageLoading, setProjectUsageLoading] = useState(false);
  const selectedProjectIdRef = useRef(selectedProjectId);
  selectedProjectIdRef.current = selectedProjectId;
  /** Ignore stale usage-breakdown responses when the project dropdown changes quickly. */
  const projectScopedUsageSeqRef = useRef(0);
  /** Per-project usage cache so dropdown changes do not flash zeros while refetching. */
  const projectUsageCacheRef = useRef<Partial<Record<number, FeatureUsageNumbers>>>({});
  /** Dedupe in-flight usage-breakdown requests per project (prefetch + dropdown share the same promise). */
  const projectUsageInflightRef = useRef<Partial<Record<number, Promise<FeatureUsageNumbers | null>>>>({});
  const [searchParams, setSearchParams] = useSearchParams();
  const loadSubscriptionRef = useRef<() => Promise<void>>(async () => {});
  const projectZoomMenu = useZoomSafeSelectMenu();

  const fetchAndCacheProjectUsage = useCallback(async (pid: number): Promise<FeatureUsageNumbers | null> => {
    const cached = projectUsageCacheRef.current[pid];
    if (cached) {
      return cached;
    }
    const inflight = projectUsageInflightRef.current[pid];
    if (inflight) {
      return inflight;
    }
    const promise = (async () => {
      try {
        const breakdown = await apiService.getSubscriptionProjectUsageBreakdown(pid).catch(() => ({}));
        const usage = parseUsageBreakdown(breakdown as Record<string, unknown>);
        projectUsageCacheRef.current[pid] = usage;
        if (selectedProjectIdRef.current === pid) {
          setSelectedProjectUsage(usage);
          setProjectUsageLoading(false);
        }
        return usage;
      } catch {
        if (selectedProjectIdRef.current === pid && !projectUsageCacheRef.current[pid]) {
          const empty: FeatureUsageNumbers = {
            collections: 0,
            requests: 0,
            datasets: 0,
            variables: 0,
            environments: 0,
            assertions: 0,
            testSuites: 0,
          };
          setSelectedProjectUsage(empty);
          setProjectUsageLoading(false);
        }
        return null;
      } finally {
        delete projectUsageInflightRef.current[pid];
      }
    })();
    projectUsageInflightRef.current[pid] = promise;
    return promise;
  }, []);

  const prefetchProjectUsageBreakdowns = useCallback(
    (projectList: Project[]) => {
      projectList.forEach((project) => {
        const pid = Number(project.id);
        if (!Number.isNaN(pid)) {
          void fetchAndCacheProjectUsage(pid);
        }
      });
    },
    [fetchAndCacheProjectUsage]
  );

  const loadProjectScopedUsage = useCallback(async () => {
    const pid = selectedProjectIdRef.current;
    if (pid === 'all') {
      setSelectedProjectUsage(null);
      setProjectUsageLoading(false);
      return;
    }
    const seq = ++projectScopedUsageSeqRef.current;
    const cached = projectUsageCacheRef.current[pid];
    if (cached) {
      setSelectedProjectUsage(cached);
    } else {
      setSelectedProjectUsage(null);
      setProjectUsageLoading(true);
    }
    await fetchAndCacheProjectUsage(pid);
    if (seq !== projectScopedUsageSeqRef.current || selectedProjectIdRef.current !== pid) {
      return;
    }
    if (!projectUsageCacheRef.current[pid]) {
      setProjectUsageLoading(false);
    }
  }, [fetchAndCacheProjectUsage]);

  const loadSubscription = useCallback(async () => {
    const hasCache = !!readManageAccountCache<SubscriptionCache>('subscription', subscriptionCacheKey);
    try {
      if (!hasCache) {
        setLoading(true);
      }
      setError(null);
      const [response, details, featureData, projectList] = await Promise.all([
        apiService.getSubscription(),
        apiService.getSubscriptionDetails().catch(() => null),
        apiService.getSubscriptionFeatureLimits().catch(() => null),
        apiService.getSubscriptionProjects().catch(() => []),
      ]);
      if (response.error) {
        setError(response.error);
      } else if (response.subscriptionError) {
        // Still apply DB-backed plan fields; only surface non-config Stripe sync issues.
        setSubscription(response);
        const errMsg = String(response.subscriptionError);
        if (!/invalid api key/i.test(errMsg)) {
          setError(response.subscriptionError);
        }
      } else {
        setSubscription(response);
      }
      const normalizedProjects = Array.isArray(projectList) ? projectList : [];
      setProjects(normalizedProjects);
      prefetchProjectUsageBreakdowns(normalizedProjects);
      // Stale project id (e.g. after role change) yields failed breakdown and wrong counts — fall back to All Projects.
      const selId = selectedProjectIdRef.current;
      if (selId !== 'all' && !normalizedProjects.some((p) => Number(p.id) === Number(selId))) {
        setSelectedProjectId('all');
        selectedProjectIdRef.current = 'all';
      }
      const asNum = (v: string | number | undefined) => {
        if (v === undefined || v === null) return undefined;
        if (v === 'Unlimited') return -1;
        const n = Number(v);
        return Number.isNaN(n) ? undefined : n;
      };

      const normalizedFd = normalizeFeatureLimits(featureData);

      let mergedDetails: SubscriptionDetailsPayload | null = null;
      if (details && !details.error) {
        mergedDetails = { ...details };
        if (normalizedFd) {
          mergedDetails.limits = {
            ...mergedDetails.limits,
            maxProjects: asNum(normalizedFd.projects) ?? mergedDetails.limits?.maxProjects,
            maxCollections: asNum(normalizedFd.collections) ?? mergedDetails.limits?.maxCollections,
            maxRequests: asNum(normalizedFd.requests) ?? mergedDetails.limits?.maxRequests,
            maxDatasets: asNum(normalizedFd.dataSets) ?? mergedDetails.limits?.maxDatasets,
            maxVariables: asNum(normalizedFd.variables) ?? mergedDetails.limits?.maxVariables,
            maxEnvironments: mergedDetails.limits?.maxEnvironments ?? asNum(normalizedFd.environments),
            maxAssertions: asNum(normalizedFd.assertions) ?? mergedDetails.limits?.maxAssertions,
            maxTestSuites: asNum(normalizedFd.testSuites) ?? mergedDetails.limits?.maxTestSuites,
          };
        }
      } else if (normalizedFd) {
        mergedDetails = {
          plan: response?.planType,
          limits: {
            maxProjects: asNum(normalizedFd.projects),
            maxCollections: asNum(normalizedFd.collections),
            maxRequests: asNum(normalizedFd.requests),
            maxDatasets: asNum(normalizedFd.dataSets),
            maxVariables: asNum(normalizedFd.variables),
            maxEnvironments: asNum(normalizedFd.environments),
            maxAssertions: asNum(normalizedFd.assertions),
            maxTestSuites: asNum(normalizedFd.testSuites),
          },
          usage: {
            projects: normalizedFd.usageProjects,
            collections: normalizedFd.usageCollections,
            requests: normalizedFd.usageRequests,
            datasets: normalizedFd.usageDataSets,
            variables: normalizedFd.usageVariables,
            environments: normalizedFd.usageEnvironments,
            assertions: normalizedFd.usageAssertions,
            testSuites: normalizedFd.usageTestSuites,
          },
        };
      }

      const planKey = (response?.planType || mergedDetails?.plan || '')
        .toString()
        .replace(/\s+Plan$/i, '')
        .trim()
        .toUpperCase();
      const planDefaultsForMerge = planKey ? DEFAULT_LIMIT_LABELS_BY_PLAN[planKey] : undefined;
      const normalizeMergedCap = (
        apiVal: number | undefined,
        kind: 'collections' | 'requests' | 'dataSets' | 'variables' | 'assertions'
      ): number | undefined => {
        if (apiVal === undefined) return undefined;
        const maxP =
          mergedDetails?.limits?.maxProjects ??
          asNum(normalizedFd?.projects) ??
          parseLimitNumStr(planDefaultsForMerge?.projects);
        const resolved = resolvePerProjectResourceCap(
          planKey,
          kind,
          apiVal,
          null,
          planDefaultsForMerge,
          maxP
        );
        return resolved ?? apiVal;
      };

      if (mergedDetails?.limits && planKey) {
        mergedDetails = {
          ...mergedDetails,
          limits: {
            ...mergedDetails.limits,
            maxCollections: normalizeMergedCap(mergedDetails.limits.maxCollections, 'collections'),
            maxRequests: normalizeMergedCap(mergedDetails.limits.maxRequests, 'requests'),
            maxDatasets: normalizeMergedCap(mergedDetails.limits.maxDatasets, 'dataSets'),
            maxVariables: normalizeMergedCap(mergedDetails.limits.maxVariables, 'variables'),
            maxAssertions: normalizeMergedCap(mergedDetails.limits.maxAssertions, 'assertions'),
          },
        };
      }

      let nextPlanLimitLabels: PlanLimitLabelStrings | null = null;
      let nextFeatureUsage: FeatureUsageNumbers | null = null;

      if (normalizedFd) {
        nextPlanLimitLabels = {
          projects: normalizedFd.projects,
          collections: normalizedFd.collections,
          requests: normalizedFd.requests,
          dataSets: normalizedFd.dataSets,
          variables: normalizedFd.variables,
          environments:
            mergedDetails?.limits?.maxEnvironments != null &&
            mergedDetails.limits.maxEnvironments >= 0
              ? String(mergedDetails.limits.maxEnvironments)
              : normalizedFd.environments,
          assertions: normalizedFd.assertions,
          testSuites: normalizedFd.testSuites,
        };
        nextFeatureUsage = {
          projects: normalizedFd.usageProjects,
          collections: normalizedFd.usageCollections,
          requests: normalizedFd.usageRequests,
          datasets: normalizedFd.usageDataSets,
          variables: normalizedFd.usageVariables,
          environments: normalizedFd.usageEnvironments,
          assertions: normalizedFd.usageAssertions,
          testSuites: normalizedFd.usageTestSuites,
        };
      } else {
        const fromAcc = labelsFromAccountSubscription(response);
        const fromLim = planLabelsFromLimits(mergedDetails?.limits);
        const fallback = planKey ? DEFAULT_LIMIT_LABELS_BY_PLAN[planKey] ?? null : null;
        nextPlanLimitLabels = fromAcc ?? fromLim ?? fallback;
        nextFeatureUsage = {
          projects: mergedDetails?.usage?.projects ?? response?.projectsUsed,
          collections: mergedDetails?.usage?.collections ?? response?.collectionsUsed,
          requests: mergedDetails?.usage?.requests ?? response?.requestsUsed,
          datasets: mergedDetails?.usage?.datasets ?? response?.datasetsUsed,
          variables: mergedDetails?.usage?.variables ?? response?.variablesUsed,
          environments: mergedDetails?.usage?.environments ?? response?.environmentsUsed,
          assertions: mergedDetails?.usage?.assertions ?? response?.assertionsUsed,
          testSuites: mergedDetails?.usage?.testSuites ?? response?.testSuitesUsed,
        };
      }

      setPlanLimitLabels(nextPlanLimitLabels);
      setFeatureUsage(nextFeatureUsage);
      setSubscriptionDetails(mergedDetails);

      if (!response.error && !response.subscriptionError) {
        writeManageAccountCache('subscription', subscriptionCacheKey, {
          subscription: response,
          subscriptionDetails: mergedDetails,
          planLimitLabels: nextPlanLimitLabels,
          featureUsage: nextFeatureUsage,
          projects: normalizedProjects,
        });
      }

      setLoading(false);
      if (selectedProjectIdRef.current !== 'all') {
        void loadProjectScopedUsage();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load subscription details');
      setLoading(false);
    }
  }, [fetchAndCacheProjectUsage, loadProjectScopedUsage, prefetchProjectUsageBreakdowns, subscriptionCacheKey]);

  loadSubscriptionRef.current = loadSubscription;

  const refreshSubscriptionState = useCallback(async () => {
    clearManageAccountCache('subscription', subscriptionCacheKey);
    await loadSubscription();
    try {
      const v = await apiService.getSubscriptionView();
      setSubscriptionView(v);
    } catch {
      setSubscriptionView(null);
    }
    window.dispatchEvent(new Event(MANAGE_ACCOUNT_SUBSCRIPTION_CHANGED_EVENT));
  }, [loadSubscription, subscriptionCacheKey]);

  useEffect(() => {
    void loadSubscription();
  }, [loadSubscription]);

  useEffect(() => {
    if (searchParams.get('tenantAddonRefresh') === 'true') {
      // Drop stale Manage Account cache so Feature Limits + add-on cards refetch effective caps.
      clearManageAccountCache('subscription', subscriptionCacheKey);
      void loadSubscription();
      window.dispatchEvent(new Event(TENANT_ADDON_REFRESH_EVENT));
      const next = new URLSearchParams(searchParams);
      next.delete('tenantAddonRefresh');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, loadSubscription, subscriptionCacheKey]);

  // Auto-hide success message after 10 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [success]);

  // Auto-hide error message after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (projects.length > 0) {
      prefetchProjectUsageBreakdowns(projects);
    }
  }, [projects, prefetchProjectUsageBreakdowns]);

  useEffect(() => {
    loadProjectScopedUsage();
  }, [selectedProjectId, loadProjectScopedUsage]);

  useEffect(() => {
    const onAddonRefresh = () => {
      void loadSubscriptionRef.current();
    };
    window.addEventListener(TENANT_ADDON_REFRESH_EVENT, onAddonRefresh);
    return () => window.removeEventListener(TENANT_ADDON_REFRESH_EVENT, onAddonRefresh);
  }, []);

  useEffect(() => {
    const onVariablesChanged = () => {
      // Drop stale per-project usage and session cache so Feature Limits refetch immediately.
      projectUsageCacheRef.current = {};
      projectUsageInflightRef.current = {};
      clearManageAccountCache('subscription', subscriptionCacheKey);
      void loadSubscriptionRef.current();
      if (selectedProjectIdRef.current !== 'all') {
        void loadProjectScopedUsage();
      }
    };
    window.addEventListener(ENVIRONMENT_VARIABLES_CHANGED_EVENT, onVariablesChanged);
    return () => window.removeEventListener(ENVIRONMENT_VARIABLES_CHANGED_EVENT, onVariablesChanged);
  }, [loadProjectScopedUsage, subscriptionCacheKey]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      if (selectedProjectIdRef.current === 'all') {
        void loadSubscriptionRef.current();
      } else {
        void loadProjectScopedUsage();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadProjectScopedUsage]);

  const usagePct = (usage: number, limit: number | undefined | null) => {
    if (limit == null || limit < 0 || limit === 0) return 0;
    return Math.min(100, (usage / limit) * 100);
  };

  const parseLimitNum = (s: string | undefined): number | null => parseLimitNumStr(s);

  /**
   * Limits (FREE, STANDARD, PROFESSIONAL):
   * - Project-scoped resources show per-project cap when one project is selected.
   * - "All Projects" shows aggregate cap = per-project cap × maxProjects (e.g. PRO: 500 collections × 10 = 5000).
   * - Data sets, variables, and assertions use the same per-project × maxProjects rule on standard tiers.
   * - Environments and test plans use per-project caps; "All Projects" scales by maxProjects (and environment add-ons on usage meter).
   */
  const displayFeatureLimit = (
    kind:
      | 'projects'
      | 'collections'
      | 'requests'
      | 'datasets'
      | 'assertions'
      | 'variables'
      | 'environments'
      | 'testSuites'
  ): string | undefined => {
    const scopeAllProjects = selectedProjectId === 'all';
    const lim = subscriptionDetails?.limits;
    const planKey = (subscriptionDetails?.plan || subscription?.planType || '')
      .toString()
      .replace(/\s+Plan$/i, '')
      .trim()
      .toUpperCase();
    const planDefaults = planKey ? DEFAULT_LIMIT_LABELS_BY_PLAN[planKey] : undefined;

    /** Normalize API values (numbers or numeric strings) and fall back to labels / plan defaults. */
    const coalesce = (
      n: number | string | undefined | null,
      label: string | undefined,
      defLabel: string | undefined
    ): number | null => {
      if (typeof n === 'number' && !Number.isNaN(n)) return n;
      if (n != null && n !== '') {
        const parsed = Number(n);
        if (!Number.isNaN(parsed)) return parsed;
      }
      return parseLimitNum(label) ?? parseLimitNum(defLabel);
    };

    const P = coalesce(lim?.maxProjects, planLimitLabels?.projects, planDefaults?.projects);
    const limNum = (v: number | undefined) =>
      typeof v === 'number' && !Number.isNaN(v) ? v : null;
    const Cp = resolvePerProjectResourceCap(
      planKey,
      'collections',
      limNum(lim?.maxCollections),
      planLimitLabels,
      planDefaults,
      P
    );
    const Rp = resolvePerProjectResourceCap(
      planKey,
      'requests',
      limNum(lim?.maxRequests),
      planLimitLabels,
      planDefaults,
      P
    );
    const Ds = resolvePerProjectResourceCap(
      planKey,
      'dataSets',
      limNum(lim?.maxDatasets),
      planLimitLabels,
      planDefaults,
      P
    );
    const Vs = resolvePerProjectResourceCap(
      planKey,
      'variables',
      limNum(lim?.maxVariables),
      planLimitLabels,
      planDefaults,
      P
    );
    const As = resolvePerProjectResourceCap(
      planKey,
      'assertions',
      limNum(lim?.maxAssertions),
      planLimitLabels,
      planDefaults,
      P
    );
    const Ep = resolvePerProjectResourceCap(
      planKey,
      'environments',
      limNum(lim?.maxEnvironments),
      planLimitLabels,
      planDefaults,
      P
    );
    const Tsp = resolvePerProjectResourceCap(
      planKey,
      'testSuites',
      limNum(lim?.maxTestSuites),
      planLimitLabels,
      planDefaults,
      P
    );

    const asLabel = (n: number | null): string | undefined => {
      if (n == null) return undefined;
      if (n < 0) return 'Unlimited';
      return String(n);
    };

    const projectsFactor = P != null && P > 0 ? P : null;

    switch (kind) {
      case 'projects': {
        /* Organization-wide project cap (same for all plans; not scoped to dropdown). */
        if (P == null) return undefined;
        if (P < 0) return 'Unlimited';
        return asLabel(P);
      }
      case 'collections': {
        if (Cp == null) return undefined;
        if (Cp < 0) return 'Unlimited';
        if (!scopeAllProjects) return asLabel(Cp);
        if (projectsFactor == null) return asLabel(Cp);
        return asLabel(Cp * projectsFactor);
      }
      case 'requests': {
        if (Rp == null) return undefined;
        if (Rp < 0) return 'Unlimited';
        if (!scopeAllProjects) return asLabel(Rp);
        if (projectsFactor == null) return asLabel(Rp);
        return asLabel(Rp * projectsFactor);
      }
      case 'datasets': {
        if (Ds == null) return undefined;
        if (Ds < 0) return 'Unlimited';
        if (!scopeAllProjects) return asLabel(Ds);
        if (projectsFactor == null) return asLabel(Ds);
        return asLabel(Ds * projectsFactor);
      }
      case 'assertions': {
        if (As == null) return undefined;
        if (As < 0) return 'Unlimited';
        if (!scopeAllProjects) return asLabel(As);
        if (projectsFactor == null) return asLabel(As);
        return asLabel(As * projectsFactor);
      }
      case 'variables': {
        if (Vs == null) return undefined;
        if (Vs < 0) return 'Unlimited';
        if (!scopeAllProjects) return asLabel(Vs);
        if (projectsFactor == null) return asLabel(Vs);
        return asLabel(Vs * projectsFactor);
      }
      case 'environments': {
        if (Ep == null) return undefined;
        if (Ep < 0) return 'Unlimited';
        if (!scopeAllProjects) return asLabel(Ep);
        if (projectsFactor == null) return asLabel(Ep);
        return asLabel(Ep * projectsFactor);
      }
      case 'testSuites': {
        if (Tsp == null) return undefined;
        if (Tsp < 0) return 'Unlimited';
        if (!scopeAllProjects) return asLabel(Tsp);
        if (projectsFactor == null) return asLabel(Tsp);
        return asLabel(Tsp * projectsFactor);
      }
      default:
        return undefined;
    }
  };

  const renderFeatureLimitRow = (
    label: string,
    usageVal: number | undefined,
    limitStr: string | undefined,
    usagePending = false
  ) => {
    const pending = usagePending && usageVal === undefined;
    const u = usageVal ?? 0;
    const limLabel = limitStr ?? '';
    const l = parseLimitNum(limitStr);
    const over = !pending && l != null && l >= 0 && u > l;
    return (
      <Box key={label} sx={{ mb: 1.5 }}>
        <Typography
          variant="body2"
          sx={{ mb: 0.5, color: over ? 'error.main' : 'text.primary' }}
        >
          {label}: {pending ? '…' : u} / {limLabel}
        </Typography>
        {over && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5 }}>
            Plan limit exceeded. Reduce usage or upgrade your plan.
          </Typography>
        )}
        {pending ? (
          <LinearProgress variant="indeterminate" />
        ) : l != null && l >= 0 ? (
          <LinearProgress
            variant="determinate"
            value={usagePct(u, l)}
            color={over ? 'error' : 'primary'}
            sx={{ height: 6, borderRadius: 1 }}
          />
        ) : null}
      </Box>
    );
  };

  const handleCancelSubscription = async () => {
    try {
      setProcessing(true);
      setError(null);
      // Cancel at period end (Stripe); access remains until validUntil; workspace becomes read-only for new data.
      const response = await apiService.cancelSubscription(false);
      if (response.error) {
        // If subscription was already cancelled, treat it as success and refresh
        if (response.error.includes('already been cancelled') || response.error.includes('already cancelled')) {
          setSuccess('Subscription was already cancelled. Status updated.');
          setCancelDialogOpen(false);
          await refreshSubscriptionState();
        } else {
          setError(response.error);
        }
      } else {
        setSuccess(response.message || 'Subscription cancellation processed');
        setCancelDialogOpen(false);
        await refreshSubscriptionState();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to cancel subscription';
      // If subscription was already cancelled, treat it as success and refresh
      if (errorMessage.includes('already been cancelled') || errorMessage.includes('already cancelled')) {
        setSuccess('Subscription was already cancelled. Status updated.');
        setCancelDialogOpen(false);
        await refreshSubscriptionState();
      } else {
        setError(errorMessage);
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setProcessing(true);
      setError(null);
      const response = await apiService.reactivateSubscription();
      if (response.error) {
        setError(response.error);
      } else {
        setSuccess(response.message || 'Subscription reactivated successfully');
        await refreshSubscriptionState();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reactivate subscription');
    } finally {
      setProcessing(false);
    }
  };

  const buildActiveSubscriptionMessage = () => {
    const planName = subscriptionDetails?.plan || subscription?.planType || 'Current';
    const endSec =
      subscription?.currentPeriodEnd ??
      subscription?.current_period_end ??
      subscription?.nextBillingDate;
    if (endSec != null) {
      const remainingDays = Math.max(
        0,
        Math.ceil((endSec * 1000 - Date.now()) / (1000 * 60 * 60 * 24))
      );
      const dayLabel = remainingDays === 1 ? 'day' : 'days';
      return `You already have an active subscription. Your ${planName} plan is active with ${remainingDays} ${dayLabel} remaining.`;
    }
    return `You already have an active subscription. Your ${planName} plan is currently active.`;
  };

  const showAlreadyActiveSubscription = (message: string) => {
    setError(null);
    setSuccess(null);
    setInfo(message);
  };

  const handleResubscribe = async () => {
    const stillHasPaidAccess = hasActivePaidPlan || (isCancelledUntilPeriodEnd && !isExpiredPlan);
    if (stillHasPaidAccess) {
      showAlreadyActiveSubscription(buildActiveSubscriptionMessage());
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      setInfo(null);
      const response = await apiService.resubscribeSubscription();
      const responseMessage = response.error || response.message;
      if (response.error || response.success === false) {
        if (responseMessage?.includes('You already have an active subscription')) {
          showAlreadyActiveSubscription(responseMessage);
        } else {
          setError(responseMessage || 'Failed to start re-subscribe');
        }
      } else if (response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      } else {
        setError('Failed to start re-subscribe checkout');
      }
    } catch (err: any) {
      const errMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to start re-subscribe';
      if (errMessage.includes('You already have an active subscription')) {
        showAlreadyActiveSubscription(errMessage);
      } else {
        setError(errMessage);
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      setProcessing(true);
      setError(null);
      const returnUrl = `${window.location.origin}/manage-account?tab=subscription`;
      const response = await apiService.createBillingPortalSession(returnUrl);
      if (response.error) {
        setError(response.error);
      } else if (response.url) {
        window.location.href = response.url;
      } else {
        setError('Failed to open billing portal');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to open billing portal');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpgradeDowngrade = () => {
    // Navigate within the current app (tenant domain) to the Pricing page in "upgrade" mode
    // so the pricing screen can hide Free/Enterprise and only show paid plans.
    navigate('/pricing?mode=upgrade');
  };

  useEffect(() => {
    let cancelled = false;
    apiService
      .getSubscriptionView()
      .then((v) => {
        if (!cancelled) setSubscriptionView(v);
      })
      .catch(() => {
        if (!cancelled) setSubscriptionView(null);
      });
    return () => {
      cancelled = true;
    };
  }, [subscription?.displayPlan, subscription?.cancelAtPeriodEnd, subscription?.userRequestedCancelAtPeriodEnd, subscription?.subscriptionStatus]);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'cancelled':
        return 'warning';
      case 'canceled':
      case 'expired':
      case 'inactive':
        return 'error';
      case 'past_due':
        return 'warning';
      case 'trialing':
        return 'info';
      default:
        return 'default';
    }
  };

  const isCancelledUntilPeriodEnd =
    subscription?.displayPlan === 'CANCELLED' ||
    subscriptionView?.readOnly === true ||
    subscriptionView?.status === 'CANCELLED' ||
    subscription?.userRequestedCancelAtPeriodEnd === true;

  const isExpiredPlan =
    subscription?.displayPlan === 'EXPIRED' ||
    subscriptionView?.status === 'EXPIRED' ||
    subscription?.status === 'INACTIVE' ||
    subscription?.subscriptionStatus === 'expired';

  const isPastDue =
    subscription?.showPaymentWarning === true ||
    subscription?.subscriptionBillingStatus === 'PAST_DUE' ||
    subscriptionView?.status === 'PAST_DUE' ||
    subscription?.subscriptionStatus === 'past_due';

  const hasActivePaidPlan = !!subscription &&
    (subscription.planType === 'STANDARD' || subscription.planType === 'PROFESSIONAL') &&
    !isExpiredPlan &&
    subscription.displayPlan !== 'CANCELLED' &&
    !isCancelledUntilPeriodEnd &&
    (subscription.status === 'ACTIVE' || subscription.status === 'active' || subscription.subscriptionStatus === 'active');

  const formatSubscriptionDate = (epochSec?: number | null) => {
    if (epochSec == null) return null;
    return new Date(epochSec * 1000).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatValidUntilDisplay = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const subscriptionExpirationDate = formatSubscriptionDate(
    subscription?.currentPeriodEnd ??
      subscription?.current_period_end ??
      subscription?.nextBillingDate
  );

  const yearlyUpgradeOnly = subscription?.yearlyUpgradeOnly === true;

  const autoRenewalEnabled =
    subscription?.autopayEnabled === true &&
    !subscription?.cancelAtPeriodEnd &&
    !subscription?.userRequestedCancelAtPeriodEnd;

  const usageForFeature: FeatureUsageNumbers | undefined =
    featureUsage ??
    subscriptionDetails?.usage ??
    (subscription
      ? {
          projects: subscription.projectsUsed,
          collections: subscription.collectionsUsed,
          requests: subscription.requestsUsed,
          datasets: subscription.datasetsUsed,
          variables: subscription.variablesUsed,
          environments: subscription.environmentsUsed,
        }
      : undefined);

  const limitStrFromDetails = (max: number | undefined) => {
    if (max == null) return undefined;
    if (max === -1) return 'Unlimited';
    if (max >= 0) return String(max);
    return undefined;
  };

  const effectiveUsage = (usageForFeature ?? {}) as FeatureUsageNumbers;

  /** Prefer live project list count; avoids stale usage and matches dropdown. */
  const tenantProjectCount =
    projects.length > 0 ? projects.length : (effectiveUsage.projects ?? 0);

  /** Strip tenant-aggregated usage so single-project rows never inherit all-projects counts from spread. */
  const {
    datasets: _tenantDatasets,
    variables: _tenantVariables,
    assertions: _tenantAssertions,
    collections: _tenantCollections,
    requests: _tenantRequests,
    environments: _tenantEnvironments,
    testSuites: _tenantTestSuites,
    projects: _tenantProjects,
    ...usageRest
  } = effectiveUsage;

  const projectScopedUsage =
    selectedProjectId === 'all'
      ? {
          ...effectiveUsage,
          projects: tenantProjectCount,
        }
      : {
          ...usageRest,
          /* 1-based position of this project in the list (3rd row → 3/10) vs plan max projects. */
          projects: Math.max(
            1,
            projects.findIndex((p) => Number(p.id) === Number(selectedProjectId)) + 1
          ),
          collections:
            selectedProjectUsage?.collections ??
            (projectUsageLoading ? undefined : 0),
          requests:
            selectedProjectUsage?.requests ??
            (projectUsageLoading ? undefined : 0),
          datasets:
            selectedProjectUsage?.datasets ??
            (projectUsageLoading ? undefined : 0),
          variables:
            selectedProjectUsage?.variables ??
            (projectUsageLoading ? undefined : 0),
          environments:
            selectedProjectUsage?.environments ??
            (projectUsageLoading ? undefined : 0),
          assertions:
            selectedProjectUsage?.assertions ??
            (projectUsageLoading ? undefined : 0),
          testSuites:
            selectedProjectUsage?.testSuites ??
            (projectUsageLoading ? undefined : 0),
        };

  const projectUsagePending = selectedProjectId !== 'all' && projectUsageLoading;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 4 } }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Subscription Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {info && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      {/* Always show current plan + Change Plan actions, even if no subscriptionId yet */}
      <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Current Plan
                </Typography>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  {subscriptionDetails?.plan || subscription?.planType || subscription?.displayPlan || 'Unknown'} Plan
                </Typography>
                {subscriptionDetails?.contactSales && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Custom plan — unlimited features. Contact sales for plan or billing changes.
                  </Alert>
                )}
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={
                      isCancelledUntilPeriodEnd
                        ? 'Cancelled'
                        : isExpiredPlan
                        ? 'Expired'
                        : isPastDue
                        ? 'PAST_DUE'
                        : subscription?.subscriptionBillingStatus
                        ? subscription.subscriptionBillingStatus
                        : subscription?.subscriptionStatus === 'canceled'
                        ? 'Cancelled'
                        : (subscription?.subscriptionStatus?.toUpperCase() || subscription?.status || 'ACTIVE')
                    }
                    color={getStatusColor(
                      isCancelledUntilPeriodEnd
                        ? 'cancelled'
                        : isExpiredPlan
                        ? 'expired'
                        : isPastDue
                        ? 'past_due'
                        : (subscription?.status || subscription?.subscriptionStatus || 'active')
                    )}
                    size="small"
                  />
                </Box>
                {subscription?.billingCycle && (
                  <Typography variant="body2" color="text.secondary">
                    Billing Cycle: {subscription.billingCycle}
                  </Typography>
                )}
                {(subscription?.subscriptionBillingStatus || subscription?.subscriptionStatus || subscription?.status) && (
                  <Typography variant="body2" color="text.secondary">
                    Subscription Status:{' '}
                    {subscription?.subscriptionBillingStatus ||
                      subscription?.subscriptionStatus ||
                      subscription?.status}
                  </Typography>
                )}
                {subscriptionExpirationDate && (
                  <Typography variant="body2" color="text.secondary">
                    Expiration Date: {subscriptionExpirationDate}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Auto Renewal: {autoRenewalEnabled ? 'On' : 'Off'}
                </Typography>
                {yearlyUpgradeOnly && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {subscription?.yearlyUpgradeOnlyMessage ||
                      'Only yearly upgrades are available while this subscription remains active.'}
                  </Alert>
                )}
                {subscription?.licenseCount && (
                  <Typography variant="body2" color="text.secondary">
                    Licenses: {subscription.licenseCount}
                  </Typography>
                )}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Subscription Actions
                </Typography>

                {subscriptionView?.status === 'CANCELLED' && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Your subscription is cancelled and will not renew. You can use your current plan until{' '}
                    {formatValidUntilDisplay(subscriptionView.validUntil) ||
                      subscriptionExpirationDate ||
                      'the end of the current period'}
                    .
                  </Alert>
                )}

                {isPastDue && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Last renewal payment failed. Please update your payment method.
                    <Box sx={{ mt: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Payment />}
                        onClick={handleUpdatePaymentMethod}
                        disabled={processing}
                      >
                        Update Payment Method
                      </Button>
                    </Box>
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {(!isCancelledUntilPeriodEnd || isExpiredPlan) && (
                    <Button
                      variant="contained"
                      startIcon={<Upgrade />}
                      onClick={handleUpgradeDowngrade}
                    >
                      {hasActivePaidPlan ? 'Change Plan' : 'Subscribe Now'}
                    </Button>
                  )}

                  {(subscription?.planType === 'STANDARD' || subscription?.planType === 'PROFESSIONAL') &&
                   !subscriptionDetails?.contactSales && (
                    <Button
                      variant="outlined"
                      startIcon={<Autorenew />}
                      onClick={handleResubscribe}
                      disabled={processing}
                    >
                      Re-subscribe
                    </Button>
                  )}

                  {(subscription?.planType === 'STANDARD' || subscription?.planType === 'PROFESSIONAL') &&
                   subscription?.status !== 'canceled' &&
                   subscription?.subscriptionStatus !== 'canceled' && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => setCancelDialogOpen(true)}
                      disabled={processing || subscription?.userRequestedCancelAtPeriodEnd}
                    >
                      Cancel Subscription
                    </Button>
                  )}

                  {((subscriptionView?.status === 'CANCELLED' ||
                    subscription?.displayPlan === 'CANCELLED' ||
                    subscription?.subscriptionStatus === 'canceled' ||
                    subscription?.userRequestedCancelAtPeriodEnd)) && (
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<Refresh />}
                      onClick={handleReactivateSubscription}
                      disabled={processing}
                    >
                      Reactivate Plan
                    </Button>
                  )}
                </Box>

                {(subscription?.displayPlan === 'CANCELLED' || subscription?.status === 'canceled' || subscription?.subscriptionStatus === 'canceled') && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {subscription?.displayPlan === 'CANCELLED'
                      ? 'Your subscription is cancelled. To continue accessing your projects, please subscribe again.'
                      : 'Your subscription has been cancelled. To access your projects, use "Subscribe Now" above when you are ready.'}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Feature Limits
                </Typography>
                <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
                  <InputLabel id="feature-limits-project-label">Project</InputLabel>
                  <Select
                    labelId="feature-limits-project-label"
                    value={selectedProjectId === 'all' ? 'all' : String(selectedProjectId)}
                    label="Project"
                    open={projectZoomMenu.menuOpen}
                    onOpen={projectZoomMenu.onOpen}
                    onClose={projectZoomMenu.onClose}
                    MenuProps={projectZoomMenu.MenuProps}
                    onChange={(e) => {
                      const value = e.target.value;
                      const pid = value === 'all' ? 'all' : Number(value);
                      if (pid === 'all') {
                        setSelectedProjectUsage(null);
                        setProjectUsageLoading(false);
                      } else {
                        const cached = projectUsageCacheRef.current[pid];
                        if (cached) {
                          setSelectedProjectUsage(cached);
                          setProjectUsageLoading(false);
                        } else {
                          setSelectedProjectUsage(null);
                          setProjectUsageLoading(true);
                        }
                      }
                      setSelectedProjectId(pid);
                    }}
                  >
                    <MenuItem value="all">All Projects</MenuItem>
                    {projects.map((project) => (
                      <MenuItem key={project.id} value={String(project.id)} title={project.name}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {usageForFeature ? (
                  <>
                    {renderFeatureLimitRow(
                      'All Projects',
                      projectScopedUsage.projects,
                      displayFeatureLimit('projects') ??
                        planLimitLabels?.projects ??
                        limitStrFromDetails(subscriptionDetails?.limits?.maxProjects)
                    )}
                    {renderFeatureLimitRow(
                      'Collections',
                      projectScopedUsage.collections,
                      displayFeatureLimit('collections') ??
                        planLimitLabels?.collections ??
                        limitStrFromDetails(subscriptionDetails?.limits?.maxCollections),
                      projectUsagePending
                    )}
                    {renderFeatureLimitRow(
                      'Requests',
                      projectScopedUsage.requests,
                      displayFeatureLimit('requests') ??
                        planLimitLabels?.requests ??
                        limitStrFromDetails(subscriptionDetails?.limits?.maxRequests),
                      projectUsagePending
                    )}
                    {renderFeatureLimitRow(
                      'Data sets',
                      projectScopedUsage.datasets,
                      displayFeatureLimit('datasets') ??
                        planLimitLabels?.dataSets ??
                        limitStrFromDetails(subscriptionDetails?.limits?.maxDatasets),
                      projectUsagePending
                    )}
                    {renderFeatureLimitRow(
                      'Variables',
                      projectScopedUsage.variables,
                      displayFeatureLimit('variables') ??
                        planLimitLabels?.variables ??
                        limitStrFromDetails(subscriptionDetails?.limits?.maxVariables),
                      projectUsagePending
                    )}
                    {renderFeatureLimitRow(
                      'Environments',
                      projectScopedUsage.environments,
                      displayFeatureLimit('environments') ??
                        planLimitLabels?.environments ??
                        limitStrFromDetails(subscriptionDetails?.limits?.maxEnvironments),
                      projectUsagePending
                    )}
                    {renderFeatureLimitRow(
                      'Assertions',
                      projectScopedUsage.assertions,
                      displayFeatureLimit('assertions') ??
                        planLimitLabels?.assertions ??
                        limitStrFromDetails(subscriptionDetails?.limits?.maxAssertions),
                      projectUsagePending
                    )}
                    {renderFeatureLimitRow(
                      'Test Plans',
                      projectScopedUsage.testSuites,
                      displayFeatureLimit('testSuites') ??
                        planLimitLabels?.testSuites ??
                        limitStrFromDetails(subscriptionDetails?.limits?.maxTestSuites),
                      projectUsagePending
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Projects:{' '}
                      {subscription?.projectsUsed ?? 0} /{' '}
                      {subscription?.maxProjects === -1 || subscription?.maxProjects == null
                        ? 'Unlimited'
                        : subscription.maxProjects}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Collections:{' '}
                      {subscription?.collectionsUsed ?? 0} /{' '}
                      {subscription?.maxCollections === -1 || subscription?.maxCollections == null
                        ? 'Unlimited'
                        : subscription.maxCollections}
                    </Typography>
                    <Typography variant="body2">
                      Requests:{' '}
                      {subscription?.requestsUsed ?? 0} /{' '}
                      {subscription?.maxRequests === -1 || subscription?.maxRequests == null
                        ? 'Unlimited'
                        : subscription.maxRequests}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <SubscriptionAddons />
          </Grid>

          {subscription?.displayPlan !== 'EXPIRED' &&
           subscription?.displayPlan !== 'CANCELLED' &&
           subscription?.planType === 'FREE' &&
           subscription?.status !== 'canceled' &&
           subscription?.subscriptionStatus !== 'canceled' && (
            <Grid item xs={12}>
              <Alert severity="info">
                You are currently on the FREE plan. Upgrade to STANDARD or PROFESSIONAL to access premium features and manage your subscription.
              </Alert>
            </Grid>
          )}
        </Grid>

      {/* Cancel Subscription Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth={false}
        sx={sidebarAwareDialogSx(600)}
      >
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to cancel your subscription?
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Your subscription will stay active until the end of the current billing period. Auto-renew will stop; you
            can continue to use your plan without interruption until expiry.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={processing}>
            Keep Subscription
          </Button>
          <Button
            onClick={handleCancelSubscription}
            color="error"
            variant="contained"
            disabled={processing}
          >
            {processing ? <CircularProgress size={20} /> : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subscription;

